import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { merge, Observable, of, timer } from 'rxjs';
import { Auth } from 'auxilium-connect';
import { ApiService } from './api.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DebugComponent } from '../components/debug/debug.component';
import { TranslatePipe } from '../pipes/translate.pipe';
import { ConfirmDialogParams, ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { NotifyService } from './notify.service';
import { LanguageDef } from 'src/lang';
import { environment } from 'src/environments/environment';
import { sameDate } from '../utils/sameDate';

@Injectable({
	providedIn: 'root'
})
export class AppService {

	auth: Observable<Auth>;

	private _languages: {id: string, label: string}[];

	version = environment.version;

	/**
	 * an observable that emits immediately,
	 * and updates every 1 minute on the minute change
	 */
	now = merge(
		of(new Date()),
		timer((() => {
			const now = new Date(),
				sec = now.getSeconds(),
				ms = now.getMilliseconds();
			return 60000 - (sec * 1000 + ms);
		})(), 60000),
	)
	.pipe(
		map(() => new Date()),
		shareReplay(1)
	);

	// this will start with the current date/time, and update
	// when the DATE changes (eg, at midnight)
	today = this.now
		.pipe(
			distinctUntilChanged((x, y) => sameDate(x, y)),
			shareReplay(1),
		);

	constructor(
		public title: Title,
		private _api: ApiService,
		private _dialog: MatDialog,
		private _translate: TranslatePipe,
		public notify: NotifyService,
	) {
		this.auth = _api.auth;
	}

	translate(text: string, obj?: any) {
		return this._translate.transform(text, obj);
	}

	changeLanguage(to: keyof LanguageDef) {
		return this._translate.changeLanguage(to);
	}

	get language() {
		return this._translate.language;
	}

	get languages() {
		return this._languages || (this._languages = this._translate.getAsOptions());
	}

	logout() {
		return this._api.logout();
	}

	showDebug() {
		this._dialog.open(DebugComponent, {
			maxHeight: '95vh',
			maxWidth: '95vw',
			width: '95vw',
			height: '95vh',
		});
	}

	showLogin() {
		return this._api.showLogin();
	}

	confirm(data: ConfirmDialogParams, config?: MatDialogConfig) {
		return this._dialog
			.open(ConfirmDialogComponent, Object.assign({}, config, {data}))
			.afterClosed()
			.pipe(
				map(r => !!r)
			);
	}

}
